import Padding from '@/components/padding/Padding';
import usePrefersDark from '@/hooks/usePrefersDark';
import instagramLogo from '@/images/instagram-logo.png';
import mailchimpLogo from '@/images/mailchimp-logo.svg';
import stripeLogo from '@/images/stripe-logo.svg';
import { Heading, Text } from '@chakra-ui/react';
import React, { useState } from 'react';

const Integrations = (): React.JSX.Element => {
  const prefersDark = usePrefersDark();
  const [expandedIndex, setExpandedIndex] = useState(0);

  const handleToggle = (index: number) => setExpandedIndex(index);

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setExpandedIndex(index);
    }
  };

  return (
    <Padding verticalPadding={20} backgroundColor={prefersDark ? '#ffffff09' : undefined}>
      <Heading fontSize="4xl" style={{ fontFamily: 'Bebas Neue' }}>
        Optional Integrations
      </Heading>
      <Text fontSize="lg" style={{ fontFamily: 'Lato' }} className="mt-2 lg:w-[70%]">
        Extend Olympiq with powerful integrations that work for you. These optional add-ons connect
        seamlessly with the platform, giving your club extra tools out of the box.
      </Text>

      {/* Bento Grid */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-md:gap-0 gap-4 md:auto-rows-[180px]">
        {/* Mailchimp - Large card spanning 2 columns on lg */}
        <div
          role="button"
          tabIndex={0}
          className="lg:col-span-2 rounded-3xl max-md:rounded-b-none max-md:cursor-pointer bg-[#FFE01B] dark:bg-[#FFE01B]/70 p-5 md:p-6 flex flex-col justify-between overflow-hidden relative group md:hover:scale-[1.02] transition-transform duration-300"
          onClick={() => handleToggle(0)}
          onKeyDown={(e) => handleKeyDown(0, e)}
        >
          <div>
            <h3 className="text-2xl font-bold font-bebas text-neutral-900 flex items-center gap-3">
              <img src={mailchimpLogo} alt="Mailchimp logo" className="h-8 w-8 object-contain" />
              Mailchimp
              <span
                className={`md:hidden ml-auto text-2xl font-light transition-transform duration-300 ${
                  expandedIndex === 0 ? 'rotate-45' : ''
                }`}
              >
                +
              </span>
            </h3>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out md:max-h-40 md:opacity-100 ${
                expandedIndex === 0 ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <p className="mt-2 text-base md:text-sm text-neutral-800 leading-relaxed">
                Keep your members informed with professional newsletters. Olympiq syncs your
                membership list directly with Mailchimp, so your mailing list is always up to date.
              </p>
            </div>
          </div>
          <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-black/5 rounded-full" />
        </div>

        {/* Instagram - Square card */}
        <div
          role="button"
          tabIndex={0}
          className="rounded-3xl max-md:rounded-none max-md:cursor-pointer bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737] dark:from-[#833AB4]/70 dark:via-[#E1306C]/70 dark:to-[#F77737]/70 p-5 md:p-6 flex flex-col justify-between overflow-hidden relative group md:hover:scale-[1.02] transition-transform duration-300"
          onClick={() => handleToggle(1)}
          onKeyDown={(e) => handleKeyDown(1, e)}
        >
          <div>
            <h3 className="text-2xl font-bold font-bebas text-white flex items-center gap-3">
              <img
                src={instagramLogo}
                alt="Instagram logo"
                className="h-8 w-8 object-contain drop-shadow-lg"
              />
              Instagram
              <span
                className={`md:hidden ml-auto text-2xl font-light transition-transform duration-300 ${
                  expandedIndex === 1 ? 'rotate-45' : ''
                }`}
              >
                +
              </span>
            </h3>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out md:max-h-40 md:opacity-100 ${
                expandedIndex === 1 ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <p className="mt-2 text-base md:text-sm text-white/90 leading-relaxed">
                Keep your website fresh. Connect your club Instagram and latest posts appear
                automatically. Give us your instagram handle, and we&apos;ll do the rest for you!
              </p>
            </div>
          </div>
          <div className="absolute -top-6 -left-6 w-24 h-24 bg-white/10 rounded-full" />
        </div>

        {/* Stripe - Wide card spanning full width */}
        <div
          role="button"
          tabIndex={0}
          className="md:col-span-2 lg:col-span-3 rounded-3xl max-md:rounded-t-none max-md:cursor-pointer bg-[#5439fe] dark:bg-[#5439fe]/70 p-5 md:p-6 flex flex-col justify-between overflow-hidden relative group md:hover:scale-[1.02] transition-transform duration-300"
          onClick={() => handleToggle(2)}
          onKeyDown={(e) => handleKeyDown(2, e)}
        >
          <div>
            <h3 className="text-2xl font-bold font-bebas text-white flex items-center gap-3">
              <img src={stripeLogo} alt="Stripe logo" className="h-6 w-6 object-contain" />
              Stripe
              <span
                className={`md:hidden ml-auto text-2xl font-light transition-transform duration-300 ${
                  expandedIndex === 2 ? 'rotate-45' : ''
                }`}
              >
                +
              </span>
            </h3>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out md:max-h-40 md:opacity-100 ${
                expandedIndex === 2 ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <p className="mt-2 text-base md:text-sm text-white/90 leading-relaxed max-w-2xl">
                Secure, reliable payment processing built in. Olympiq uses Stripe to handle all
                transactions, so your members can pay with confidence using cards, Apple Pay, or
                Google Pay. Funds are paid out daily to your bank account.
              </p>
            </div>
          </div>
          <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/5 rounded-full" />
          <div className="absolute top-4 right-32 w-20 h-20 bg-white/5 rounded-full hidden md:block" />
        </div>
      </div>
    </Padding>
  );
};

export default Integrations;
